/**
 * /category/:slug — dedicated landing page for one category. Reads
 * better than a filtered homepage query-param for SEO (canonical URL,
 * category-specific H1, meta tags, JSON-LD ItemList schema so Google
 * can render the domain grid as rich results).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import Seo from "@/components/Seo";

const CATEGORY_META = {
  ai:            { key: "AI", tagline: "Names built for the next wave of AI infrastructure, agents, and applied models." },
  saas:          { key: "SaaS", tagline: "Two-syllable, brand-first .coms and .ios for product-led SaaS founders." },
  finance:       { key: "Finance", tagline: "Trust-first domain names for fintech, wealth, and payments." },
  "real-estate": { key: "Real Estate", tagline: "Regional and category-defining names for prop-tech and brokerages." },
  crypto:        { key: "Crypto", tagline: "Sharp, memorable domains for exchanges, wallets, and DeFi." },
  health:        { key: "Health", tagline: "Warm, credible names for wellness, care, and health-tech." },
  commerce:      { key: "Commerce", tagline: "DTC-ready, easy-to-say domains for e-commerce brands." },
  media:         { key: "Media", tagline: "Editorial-grade names for publishers, newsletters, and creator brands." },
  travel:        { key: "Travel", tagline: "Aspirational, one-word names for hospitality and travel-tech." },
  other:         { key: "Other", tagline: "The rest of the catalog — one-of-one gems that don't fit a bucket." },
};

export default function CategoryPage() {
  const { slug } = useParams();
  const meta = CATEGORY_META[slug?.toLowerCase()];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroUrl, setHeroUrl] = useState("");
  const [focal, setFocal] = useState({ x: 50, y: 50 });
  const [accent, setAccent] = useState("#3ee8d3");

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    api.get("/listings/search", { params: { category: meta.key, limit: 60 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Fetch the editorial hero (if the admin has uploaded one) so we can
    // render a full-bleed band above the H1. Same endpoint as the header
    // mega-menu already uses, so it's cached across renders.
    api.get("/listings/categories")
      .then((r) => {
        const match = (r.data?.categories || []).find((c) => c.key === meta.key);
        setHeroUrl(match?.hero_url || "");
        setFocal({
          x: typeof match?.focal_x === "number" ? match.focal_x : 50,
          y: typeof match?.focal_y === "number" ? match.focal_y : 50,
        });
        if (match?.accent) setAccent(match.accent);
      })
      .catch(() => setHeroUrl(""));
  }, [meta]);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const fullHero = heroUrl?.startsWith("/api/") ? `${backend}${heroUrl}` : heroUrl;

  // Build ItemList JSON-LD so Google can render the grid as rich results.
  const jsonLd = useMemo(() => {
    if (!items.length || !meta) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `${meta.key} domain names for sale`,
      "numberOfItems": items.length,
      "itemListElement": items.slice(0, 30).map((d, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Product",
          "name": d.domain,
          "url": `${window.location.origin}/l/${d.domain}`,
          "offers": {
            "@type": "Offer",
            "priceCurrency": d.currency || "EUR",
            "price": d.price || 0,
            "availability": "https://schema.org/InStock",
          },
        },
      })),
    };
  }, [items, meta]);

  if (!meta) {
    return (
      <>
        <SiteHeader />
        <main className="cd-container" style={{ padding: "80px 0", minHeight: "60vh", textAlign: "center" }}>
          <h1 className="cd-display" style={{ fontSize: 40 }}>Unknown category</h1>
          <p className="cd-lead" style={{ marginTop: 20 }}>
            <Link to="/">← Back to homepage</Link>
          </p>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${meta.key} domain names for sale · CasperDomains`}
        description={meta.tagline}
      />
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      <SiteHeader />
      {fullHero ? (
        <>
          <div
            data-testid="category-hero-band"
            style={{
              width: "100%",
              aspectRatio: "16 / 5",
              maxHeight: 420,
              backgroundImage: `linear-gradient(180deg, ${accent}00 55%, ${accent}2e 100%), url(${fullHero})`,
              backgroundSize: "cover",
              backgroundPosition: `${focal.x}% ${focal.y}%`,
              backgroundColor: "var(--cd-gray-100)",
            }}
          />
          <div
            data-testid="category-accent-rule"
            style={{ height: 4, background: `linear-gradient(90deg, var(--cd-navy-950), ${accent})` }}
          />
        </>
      ) : (
        <div
          data-testid="category-accent-rule"
          style={{ height: 4, background: `linear-gradient(90deg, var(--cd-navy-950), ${accent})` }}
        />
      )}
      <main className="cd-container" style={{ paddingTop: fullHero ? 48 : 80, paddingBottom: 80, minHeight: "70vh" }}>
        <div style={{ marginBottom: 44, maxWidth: 800 }}>
          <div
            data-testid="category-eyebrow"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 11, fontWeight: 700, letterSpacing: ".1em",
              color: "var(--cd-navy-950)", textTransform: "uppercase", marginBottom: 14,
              padding: "5px 12px", borderRadius: 999,
              background: `${accent}22`, border: `1px solid ${accent}66`,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />
            Category · {meta.key}
          </div>
          <h1
            className="cd-display"
            style={{ fontSize: "clamp(44px, 5.5vw, 72px)", lineHeight: 1.05 }}
            data-testid="category-page-title"
          >
            {meta.key} domain names for sale.
          </h1>
          <p className="cd-lead" style={{ marginTop: 24 }}>{meta.tagline}</p>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 80, color: "var(--cd-gray-500)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: 80, background: "var(--cd-gray-50)", borderRadius: 18, color: "var(--cd-gray-500)" }}>
            No {meta.key} domains listed yet. <Link to="/">Browse everything →</Link>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 20,
            }}
            data-testid="category-grid"
          >
            {items.map((d) => (
              <Link
                key={d.domain}
                to={`/l/${d.domain}`}
                data-testid={`category-card-${d.domain}`}
                style={{
                  display: "block", padding: "24px 22px",
                  border: "1px solid var(--cd-gray-200)", borderRadius: 14,
                  background: "white", textDecoration: "none", color: "inherit",
                  transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 24px rgba(3,20,47,0.06)"; e.currentTarget.style.borderColor = accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = "var(--cd-gray-200)"; }}
              >
                {d.is_premium && (
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".1em", color: "#92400e", background: "#fef3c7", padding: "3px 7px", borderRadius: 999, textTransform: "uppercase", marginBottom: 10, display: "inline-block" }}>
                    ★ Premium
                  </span>
                )}
                <div style={{ fontSize: 20, fontWeight: 700, color: "var(--cd-navy-950)", lineHeight: 1.2, marginBottom: 10, wordBreak: "break-word" }}>
                  {d.domain}
                </div>
                {d.ai_copy?.tagline && (
                  <div style={{ fontSize: 12, color: "var(--cd-gray-600)", marginBottom: 14, lineHeight: 1.45 }}>
                    {d.ai_copy.tagline}
                  </div>
                )}
                <div style={{ fontSize: 17, fontWeight: 600, color: "var(--cd-navy-950)" }}>
                  {(d.currency || "EUR") === "EUR" ? "€" : "$"}{(d.price || 0).toLocaleString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
      <BullseyeEmbed page="category" />
    </>
  );
}
